import AbstractAuthenticator from './abstract';
import CognitoAuthenticator from './cognito';
import ApiFactory from '../api/factory';
import NeonUser from './user/neon';
import Logger from '../logger';
import {
    RequiresTwoFactorAuthenticationCode,
    InvalidTwoFactorAuthenticationCode,
    RequiresPasswordReset
} from './constants/AuthExceptions';
import NeonExceptions from './constants/NeonExceptions';
import NeonActions from './constants/NeonActions';
import { cognito, entra } from './providers';

/**
 *
 * @class NeonAuthenticator
 * @description Performs all neon authentication
 */
class NeonAuthenticator extends AbstractAuthenticator {

    /**
     * Constructs the authenticator
     * @param {Object} config - authenticator config
     * @param {string} config.loginRedirect - login path
     * @param {Object} cognitoConfig - authenticator cognito config
     * @param {string} cognitoConfig.userPoolId - cognito user pool id
     * @param {string} cognitoConfig.appClientId - cognito appl client id
     */
    constructor(config, cognitoConfig) {
        super('neon', config);

        this.cognitoAuthenticator = new CognitoAuthenticator(cognitoConfig);
        this.logger = Logger.instance;
    }

    /**
     * This method will fetch a new token (automatically set as a cookie) and return the authenticated user as json response
     * @param {string} [authToken] - authentication token, if this is set, the neon api will automatically renew the neon token if necessary
     * @param {string} [twoFactorAuthenticationCode] - two factor authentication code, when provided it will be added to the authorise request
     * @param {string} [organisationTenantSlug] - organisationTenantSlug for multi tenancy
     * @param {number} [attemptsCount] - number of attempts to fetch the user
     *
     * @returns {Promise} promise
     */
    fetchNeonApiTokenAndUser(
        authToken = '',
        twoFactorAuthenticationCode = '',
        organisationTenantSlug = '',
        attemptsCount = 0
    ) {
        return new Promise((resolve, reject) => {
            const api = ApiFactory.get('neon');
            const headers = new Headers();

            // set the authorization header for automatic renewal, if available
            if (authToken) {
                headers.append('Authorization', authToken);
            }

            if (this.getUser() && this.getUser().getCsrfToken()) {
                headers.append('token', this.getUser().getCsrfToken());
            } else if (this.getCsrfToken()) {
                headers.append('token', this.getCsrfToken());
            }

            let twoFactorCodeSuffix = '';

            if (twoFactorAuthenticationCode) {
                twoFactorCodeSuffix = `?two_factor_form[code]=${twoFactorAuthenticationCode}`;
            }

            let endpoint = `${api.getBaseUrl()}${api.getEndpoints().authorise}`;

            if (organisationTenantSlug) {
                endpoint = `${endpoint}/${organisationTenantSlug}`;
            }

            fetch(
                `${endpoint}${twoFactorCodeSuffix}`,
                {
                    method: 'post',
                    headers,
                    credentials: 'include', // allows cookies to be sent and received
                    cache: 'no-store' // make sure no responses are cached
                }
            ).then(response => {

                // this needs to be done here since this function is the first thing to be called in CP and PP
                api.setMaintenanceDate(response.headers.get('Maintenance-Mode') || null);

                // store csrf token when when this call did not succeed
                // so we can use it in token renewal calls, until te User object is constructed again
                if (response.status !== 200 && response.headers.get('token')) {
                    if (this.getUser()) {
                        this.getUser().setCsrfToken(response.headers.get('token'));
                    } else {
                        this.setCsrfToken(response.headers.get('token'));
                    }
                }

                if (response.status === 200) {

                    // authenticate was ok, resolve with json response (user)
                    response.json().then(userResponse => {
                        if (userResponse.user) {
                            userResponse.user.csrfToken = response.headers.get('token');

                            // save and return the user
                            this.user = new NeonUser(userResponse.user);

                            // see if an authProvider was specified then store it in sessionStorage
                            if (userResponse.user?.authProvider) {
                                sessionStorage.setItem('authProvider', userResponse.user.authProvider);
                            }

                            // reset this crsf token, because it should be accessed through the User object
                            this.setCsrfToken(null);

                            resolve(this.user);
                        } else {
                            reject(new Error('Could not fetch user information while authenticating'));
                        }
                    });
                } else if (response.status === 400) {
                    const csrfToken = response.headers.get('token');

                    if (this.getUser() && this.getUser().getCsrfToken()) {
                        headers.append('token', this.getUser().getCsrfToken());
                    } else if (this.getCsrfToken()) {
                        headers.append('token', this.getCsrfToken());
                    } else {
                        headers.append('token', response?.headers?.get('token'));
                    }

                    response.json().then(userResponse => {
                        if (userResponse.user) {
                            userResponse.user.csrfToken = csrfToken;
                            this.user = new NeonUser(userResponse.user);
                            resolve(this.user);
                        } else {
                            const refreshTokenAttemptsCount = (typeof attemptsCount === 'number' ? attemptsCount + 1 : 0);

                            if (refreshTokenAttemptsCount > 3) {
                                reject(new Error('Exceeded maximum number of attempts to refresh token'));
                                return;
                            }

                            if (sessionStorage?.getItem('authProvider') === 'entraId') {
                                this.refreshTokens(organisationTenantSlug, refreshTokenAttemptsCount, 'entraId', sessionStorage.getItem('authToken') || '')
                                    .then(user => resolve(user))
                                    .catch(error => reject(error));
                            } else {
                                this.refreshTokens(organisationTenantSlug, refreshTokenAttemptsCount)
                                    .then(user => resolve(user))
                                    .catch(error => reject(error));
                            }
                        }
                    }).catch(() => {
                        reject(new Error('Could not fetch user information while authenticating'));
                    });
                } else if (response.status === 401) {
                    const refreshTokenAttemptsCount = typeof attemptsCount === 'number' ? attemptsCount + 1 : 0;

                    // ran out of refresh attempts, log out indefinitely
                    if (refreshTokenAttemptsCount > 3) {
                        reject(new Error('Exceeded maximum number of attempts to refresh token'));
                        return;
                    }

                    if (sessionStorage?.getItem('authProvider') === 'entraId') {
                        this.refreshTokens(organisationTenantSlug, refreshTokenAttemptsCount, 'entraId', sessionStorage.getItem('authToken') || '')
                            .then(user => resolve(user))
                            .catch(error => reject(error));
                    } else {
                        this.refreshTokens(organisationTenantSlug, refreshTokenAttemptsCount)
                            .then(user => resolve(user))
                            .catch(error => reject(error));
                    }
                } else if (response.status === 403) {
                    response.json().then(jsonResponse => {
                        if (jsonResponse) {
                            if (jsonResponse.action === NeonActions.REQUIRE_MFA) {
                                reject(new RequiresTwoFactorAuthenticationCode('RequiresTwoFactorAuthenticationCode'));
                            } else if (jsonResponse.errors && jsonResponse.errors[0] === NeonExceptions.INVALID_TWO_FACTOR_CODE) {
                                reject(new InvalidTwoFactorAuthenticationCode('InvalidTwoFactorAuthenticationCode'));
                            } else if (jsonResponse.action === NeonActions.REQUIRE_PW_RESET) {
                                reject(new RequiresPasswordReset('RequiresPasswordReset', jsonResponse.token));
                            }
                        }

                        let error = jsonResponse.error;

                        if (!error && jsonResponse.errors && jsonResponse.errors.length) {
                            error = jsonResponse.errors[0];
                        }

                        reject(new Error(error));
                    });

                } else if (response.status === 503) {

                    // return maintenance mode if the backend is not available
                    resolve({
                        maintenance: true
                    });
                } else {
                    response.text()
                        .catch(() => '') // fallback of response.text()
                        .then(responseText => {
                            throw new Error(`Unexpected authentication error, status: ${response.status || ''}, response: ${responseText || ''}`);
                        });
                }
            }).catch(error => {

                // unexpected result, means we are not authenticated and not trying again.
                reject(error);
            });
        });
    }

    /**
     * Refreshes tokens using the appropriate provider.
     *
     * @param {string} [organisationTenantSlug=''] - Organisation slug for multi-tenancy.
     * @param {number} attemptsCount - Number of attempts made so far for token refresh.
     * @param {string} [authProvider=null] - Optional auth provider, e.g. 'entra'; defaults to 'cognito'.
     * @param {string} [authToken=null] - The Entra auth token (only needed for Entra flow).
     * @returns {Promise<Object>} - Resolves with the authenticated user object.
     */
    refreshTokens(organisationTenantSlug = '', attemptsCount, authProvider = null, authToken = null) {
        if (authProvider === 'entraId') {
            return entra.refreshTokens.call(this, organisationTenantSlug, attemptsCount, authToken);
        }

        return cognito.refreshTokens.call(this, organisationTenantSlug, attemptsCount);
    }

    /**
     * @param {string} [organisationTenantSlug] organisationTenantSlug for multi tenancy
     * Returns the authenticated user. If a token refresh is required, then this will be handled by this method automatically
     * @returns {Promise} promise
     */
    refreshAndGetUser(organisationTenantSlug = '') {
        return new Promise((resolve, reject) => {

            // fetch the neon api token and authenticated user. Token renewal is covered in this method
            this.fetchNeonApiTokenAndUser(
                '',
                '',
                organisationTenantSlug)
                .then(user => resolve(user))
                .catch(error => reject(error));
        });
    }

    /**
     * Authenticates a user. The authentication strategies are defined in providers. The this context needs to be passed.
     *
     * @param {Object} credentials - credentials object
     * @param {string} [credentials.username] - credentials username (for Cognito)
     * @param {string} [credentials.password] - credentials password (for Cognito)
     * @param {string} [credentials.twoFactorAuthenticationCode] - two factor authentication code (for Cognito)
     * @param {string} [credentials.idToken] - The Entra idToken (for Entra)
     * @param {string} [authProvider] - Optional auth provider, e.g. 'entra'; defaults to 'cognito'.
     * @returns {Promise<Object>} - Resolves with the authenticated user object.
     */
    authenticate(credentials, authProvider = null) {
        if (authProvider === 'entraId') {
            return entra.authenticate.call(this, credentials);
        }

        return cognito.authenticate.call(this, credentials);
    }

    /**
     * Call the authenticator to authenticate a guest
     * @returns {Promise} authentication promise
     */
    authenticateGuest() {
        return new Promise((resolve, reject) => {
            const api = ApiFactory.get('neon');

            const endpoint = `${api.getBaseUrl()}${api.getEndpoints().authoriseGuest}`;

            fetch(
                endpoint,
                {
                    method: 'get',
                    credentials: 'include', // allows cookies to be sent and received
                    cache: 'no-store' // make sure no responses are cached
                }
            ).then(response => {
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    }

    /**
     * Logs out the user from the neon api (clears cookie)
     * @param {string} [type] - type of logout (user or forced)
     * @returns {Promise} promise
     */
    logout(type = 'user') {
        const api = ApiFactory.get('neon');

        return new Promise((resolve, reject) => {
            this.cognitoAuthenticator.logout().then(() => {

                fetch(
                    api.getBaseUrl() + api.getEndpoints().logout,
                    {
                        method: 'get',
                        credentials: 'include', // allows cookies to be sent and received
                        cache: 'no-store' // make sure no responses are cached
                    }
                ).then(response => {

                    // in case entra was used, better clear the sessionStorage too
                    sessionStorage.removeItem('authProvider');
                    sessionStorage.removeItem('authToken');

                    if (response.status === 200) {
                        this.logger.notice({
                            message: `${type} logout succesful`,
                            component: 'neonAuth'
                        });
                        resolve();
                    } else {
                        this.logger.error({
                            message: `Unsuccesful ${type} logout`,
                            component: 'neonAuth'
                        });
                        reject(new Error('logout failed'));
                    }

                }).catch(error => {

                    // unexpected result, logout call failed
                    reject(error);
                });
            });
        });
    }
}

export default NeonAuthenticator;
